import * as Cesium from 'cesium';

const CHASE_CAMERA_RANGE = 900;
const CHASE_CAMERA_PITCH = -12;
const CHASE_CAMERA_MIN_PITCH = -65;
const CHASE_CAMERA_MAX_PITCH = -5;

export type CesiumWorldContext = {
  viewer: Cesium.Viewer;
  miniViewer: Cesium.Viewer | null;
  pauseMiniViewer: Cesium.Viewer | null;
  setRenderOptimization: (isMenu: boolean) => void;
  setControlsEnabled: (enabled: boolean) => void;
  setCameraToPlane: (lon: number, lat: number, alt: number, heading: number, pitch: number, roll: number) => void;
  setMinimapCamera: (lon: number, lat: number, altitude: number, heading: number) => void;
  setPauseMinimapCamera: (lon: number, lat: number, altitude: number, heading: number) => void;
  destroy: () => void;
};

type InitCesiumOptions = {
  mainContainer: HTMLElement;
  minimapContainer: HTMLElement;
  pauseMinimapContainer: HTMLElement;
};

let viewer: Cesium.Viewer | null = null;
let miniViewer: Cesium.Viewer | null = null;
let pauseMiniViewer: Cesium.Viewer | null = null;

function hideCredits(target: Cesium.Viewer) {
  const widget = target as Cesium.Viewer & { _cesiumWidget?: { _creditContainer?: HTMLElement } };
  const creditContainer = widget._cesiumWidget?._creditContainer;
  if (creditContainer) {
    creditContainer.style.display = 'none';
  }
}

function applyCommonViewerSettings(target: Cesium.Viewer | null) {
  if (!target) return;

  target.scene.requestRenderMode = false;
  target.scene.maximumRenderTimeChange = 0;
  target.scene.globe.maximumScreenSpaceError = 2;
  target.resolutionScale = 0.75;

  target.scene.screenSpaceCameraController.enableRotate = false;
  target.scene.screenSpaceCameraController.enableTranslate = false;
  target.scene.screenSpaceCameraController.enableZoom = false;
  target.scene.screenSpaceCameraController.enableTilt = false;
  target.scene.screenSpaceCameraController.enableLook = false;
  target.scene.screenSpaceCameraController.maximumZoomDistance = 25_000_000;

  const globe = target.scene.globe as Cesium.Globe & {
    skipLevelOfDetail?: boolean;
    baseScreenSpaceError?: number;
    skipScreenSpaceErrorFactor?: number;
    skipLevels?: number;
  };

  globe.tileCacheSize = 2048;
  globe.preloadAncestors = true;
  globe.preloadSiblings = true;
  globe.loadingDescendantLimit = 20;
  globe.skipLevelOfDetail = true;
  globe.baseScreenSpaceError = 1024;
  globe.skipScreenSpaceErrorFactor = 16;
  globe.skipLevels = 1;

  hideCredits(target);
}

function applyMiniViewerSettings(target: Cesium.Viewer | null) {
  if (!target) return;

  target.scene.globe.enableLighting = false;
  target.scene.globe.showGroundAtmosphere = false;
  target.scene.fog.enabled = false;
  target.scene.highDynamicRange = false;
  target.scene.postProcessStages.fxaa.enabled = false;
  target.resolutionScale = 1.0;
  target.scene.globe.maximumScreenSpaceError = 2;
  target.scene.globe.baseColor = Cesium.Color.fromCssColorString('#061006');
  if (target.scene.skyAtmosphere) {
    (target.scene.skyAtmosphere as any).show = false;
  }
}

async function applyMinimapImagery(target: Cesium.Viewer | null) {
  if (!target) return;

  target.imageryLayers.removeAll();

  try {
    const worldImagery = await Cesium.createWorldImageryAsync({
      style: Cesium.IonWorldImageryStyle.AERIAL,
    });
    target.imageryLayers.addImageryProvider(worldImagery);
    target.scene.requestRender();
    return;
  } catch (error) {
    console.warn('FlightSim aerial minimap imagery failed to initialize; trying OpenStreetMap fallback', error);
  }

  try {
    const osm = new Cesium.OpenStreetMapImageryProvider({
      url: 'https://a.tile.openstreetmap.org/',
    });
    target.imageryLayers.addImageryProvider(osm);
    target.scene.requestRender();
  } catch (error) {
    console.warn('FlightSim minimap imagery fallback failed; leaving plain globe background', error);
  }
}

function createTerrain() {
  const ionToken = import.meta.env.VITE_CESIUM_ION_TOKEN;
  if (ionToken) {
    Cesium.Ion.defaultAccessToken = ionToken;
    return Cesium.Terrain.fromWorldTerrain();
  }

  return new Cesium.EllipsoidTerrainProvider();
}

function createMiniViewer(container: HTMLElement, miniOptions: ConstructorParameters<typeof Cesium.Viewer>[1]) {
  try {
    return new Cesium.Viewer(container, miniOptions);
  } catch (error) {
    console.warn('FlightSim minimap viewer failed to initialize; continuing without that minimap', error);
    return null;
  }
}

export function initCesium({ mainContainer, minimapContainer, pauseMinimapContainer }: InitCesiumOptions): CesiumWorldContext {
  destroyCesium();

  const mainTerrain = createTerrain();

  viewer = new Cesium.Viewer(mainContainer, {
    terrain: mainTerrain as never,
    timeline: false,
    animation: false,
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    infoBox: false,
    sceneModePicker: false,
    selectionIndicator: false,
    navigationHelpButton: false,
    fullscreenButton: false,
    shouldAnimate: false,
  });

  const miniOptions = {
    terrain: null as never,
    timeline: false,
    animation: false,
    baseLayerPicker: false,
    geocoder: false,
    homeButton: false,
    infoBox: false,
    sceneModePicker: false,
    selectionIndicator: false,
    navigationHelpButton: false,
    fullscreenButton: false,
    shouldAnimate: false,
    skyBox: false as const,
    skyAtmosphere: false as const,
    contextOptions: {
      webgl: {
        preserveDrawingBuffer: true,
      },
    },
  };

  miniViewer = createMiniViewer(minimapContainer, miniOptions);
  pauseMiniViewer = createMiniViewer(pauseMinimapContainer, miniOptions);

  [viewer, miniViewer, pauseMiniViewer].forEach(applyCommonViewerSettings);
  [miniViewer, pauseMiniViewer].forEach(applyMiniViewerSettings);
  void applyMinimapImagery(miniViewer);
  void applyMinimapImagery(pauseMiniViewer);

  viewer.scene.globe.enableLighting = true;
  viewer.scene.highDynamicRange = false;
  viewer.scene.postProcessStages.fxaa.enabled = true;
  viewer.scene.skyAtmosphere = new Cesium.SkyAtmosphere();
  viewer.scene.fog.enabled = true;
  viewer.scene.fog.density = 0.0001;

  setControlsEnabled(false);

  return {
    viewer,
    miniViewer,
    pauseMiniViewer,
    setRenderOptimization,
    setControlsEnabled,
    setCameraToPlane,
    setMinimapCamera,
    setPauseMinimapCamera,
    destroy: destroyCesium,
  };
}

export function setRenderOptimization(isMenu: boolean) {
  if (!viewer) return;

  [viewer, miniViewer, pauseMiniViewer].forEach((target) => {
    if (!target) return;
    target.scene.requestRenderMode = !isMenu;
    target.scene.maximumRenderTimeChange = !isMenu ? Number.POSITIVE_INFINITY : 0;
  });
}

export function setControlsEnabled(enabled: boolean) {
  if (!viewer) return;
  const controls = viewer.scene.screenSpaceCameraController;
  controls.enableRotate = enabled;
  controls.enableTranslate = enabled;
  controls.enableZoom = enabled;
  controls.enableTilt = enabled;
  controls.enableLook = enabled;
}

export function setCameraToPlane(lon: number, lat: number, alt: number, heading: number, pitch: number, roll: number) {
  if (!viewer) return;

  viewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, alt),
    orientation: {
      heading: Cesium.Math.toRadians(heading),
      pitch: Cesium.Math.toRadians(pitch),
      roll: Cesium.Math.toRadians(roll),
    },
  });

  viewer.scene.requestRender();
}

export function setMinimapCamera(lon: number, lat: number, altitude: number, heading: number) {
  if (!miniViewer || miniViewer.canvas.width === 0 || miniViewer.canvas.height === 0) return;

  miniViewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, altitude),
    orientation: {
      heading: Cesium.Math.toRadians(heading),
      pitch: Cesium.Math.toRadians(-90),
      roll: 0,
    },
  });

  miniViewer.scene.requestRender();
}

export function setPauseMinimapCamera(lon: number, lat: number, altitude: number, heading: number) {
  if (!pauseMiniViewer || pauseMiniViewer.canvas.width === 0 || pauseMiniViewer.canvas.height === 0) return;

  pauseMiniViewer.camera.setView({
    destination: Cesium.Cartesian3.fromDegrees(lon, lat, altitude),
    orientation: {
      heading: Cesium.Math.toRadians(heading),
      pitch: Cesium.Math.toRadians(-90),
      roll: 0,
    },
  });

  pauseMiniViewer.scene.requestRender();
}

export function getViewer() {
  return viewer;
}

export function getMiniViewer() {
  return miniViewer;
}

export function getPauseMiniViewer() {
  return pauseMiniViewer;
}

export function destroyCesium() {
  [viewer, miniViewer, pauseMiniViewer].forEach((target) => {
    if (target && !target.isDestroyed()) {
      target.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
      target.destroy();
    }
  });

  viewer = null;
  miniViewer = null;
  pauseMiniViewer = null;
}